import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useLocation } from '@thd-olt-component-react/router';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { ExperienceContext, useConfigService, useStore, useStoreId } from '@thd-nucleus/experience-context';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Metadata, PackagesMetadata } from '@thd-olt-component-react/metadata';
import { PackageRedirector } from '@thd-nucleus/app-render';
import { Tooltip, Typography } from '@one-thd/sui-atomic-components';
import {
  useDataModel,
  params,
  shape,
  string,
  number,
  arrayOf,
  extend,
  QueryProvider
} from '@thd-nucleus/data-sources';
import {
  Col,
  Row,
  Heading,
  Image
} from '@thd-olt-component-react/core-ui';
import { Price } from '@thd-olt-component-react/price';
import { ProductPod } from '@thd-olt-component-react/product-pod';
import { StickyWithHeaderObserver } from '@thd-olt-component-react/sticky';
import { AddToCart } from '@thd-olt-component-react/add-to-cart';
import {
  ProductSet,
  ProductSetPlaceholder,
  getPricingInfo,
  getFlatActiveProducts,
  flattenProductGroups,
  buildNewFilledArray
} from '@thd-olt-component-react/product-set';
import { FourApplianceTemplate } from '@thd-olt-component-react/dynamic-image-grid';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import {
  buildCartRequestParams,
  getCartOptions,
  getDynamicImages
} from '../../utils/appliancePackageUtils';
import { triggerPackageAnalytics } from '../../utils/digitalDataUtils'
import { APPLIANCE_PACKAGE_PRIORITIES } from '../../constants';
import '../../styles/appliance-package-page.scss';

export const AppliancePackage = () => {
  const [updatedProducts, setUpdatedProducts] = useState([]);
  const [productGroups, setProductGroups] = useState({});
  const [loadingState, setLoadingState] = useState(buildNewFilledArray(Object?.keys(productGroups).length, false));
  const [isPackageQuery, setIsPackageQuery] = useState(true);
  const [metaData, setMetaData] = useState({})
  const storeId = useStoreId();
  const { packageId } = useParams();
  const routerParams = useParams();
  const { channel } = useContext(ExperienceContext);
  const disableSSR = typeof window !== 'undefined' && window.DISABLE_SSR;
  const { isLocalized } = useStore({ varnish: disableSSR, online: false });
  const isMobile = channel === 'mobile';
  const zipCode = typeof window !== 'undefined' ? window.cookieUtils?.readBrowserCookie?.('DELIVERY_ZIP') : '';
  const location = useLocation();
  const stickyRef = useRef(null);
  const localizedAndClientLoaded = useRef(false);
  const applianceDeliveryThreshold = useConfigService('applianceDeliveryThreshold') ?? 396;
  const promoId = useConfigService('promoId');
  const { isCustomerIdentified } = useThdCustomer() || {};
  const isBrandPricingPolicyCompliant = localizedAndClientLoaded.current ? isCustomerIdentified : false;

  const packageOpts = {
    variables: {
      packageId,
      storeId,
      isBrandPricingPolicyCompliant
    },
    skip: !isPackageQuery,
    ssr: true
  };

  const productsOpts = {
    variables: {
      itemIds: getFlatActiveProducts(updatedProducts).map((product) => product.itemId),
      storeId,
      isBrandPricingPolicyCompliant
    },
    skip: isPackageQuery,
    ssr: false
  };

  const packageResponse = useDataModel('packages', packageOpts);
  const productsResponse = useDataModel('products', productsOpts);

  const { data, loading, responseLoading } = isPackageQuery ? packageResponse : productsResponse;

  if (data && !loading && !responseLoading && isLocalized) {
    localizedAndClientLoaded.current = true;
  }

  const groupProductsFromPackagesCall = (products) => {
    const productGrouping = products?.reduce((acc, item) => {
      const productGroupName = item.info?.productGroup || item.itemId;
      const product = {
        ...item,
        active: !acc[productGroupName],
        removed: false,
        swappedProducts: []
      };
      if (acc[productGroupName]) {
        acc[productGroupName].push(product);
      } else {
        acc[productGroupName] = [product];
      }
      return acc;
    }, {});
    setProductGroups(productGrouping);
  };

  /*
    TODO: Add ...matchedProduct to mergedProduct once https://thd.atlassian.net/browse/AI-1386 is done
  */
  const groupProductsFromProductsCall = (products) => {
    const productGrouping = updatedProducts.reduce((acc, item) => {
      const productGroupName = item.info?.productGroup || item.itemId;
      const matchedProduct = products.find((product) => product.itemId === item.itemId);
      if (!matchedProduct) {
        if (acc[productGroupName]) {
          acc[productGroupName].push(item);
        } else {
          acc[productGroupName] = [item];
        }
      } else if (matchedProduct) {
        const mergedProduct = {
          ...item,
          pricing: {
            ...matchedProduct.pricing,
          },
          info: {
            ...matchedProduct.info,
            label: item.info.label,
            productGroup: item.info.productGroup
          }
        };
        if (!acc[productGroupName]) {
          acc[productGroupName] = [mergedProduct];
        } else {
          acc[productGroupName].push(mergedProduct);
        }
      }
      return acc;
    }, {});
    setProductGroups(productGrouping);
  };

  const updateProducts = (products) => {
    setUpdatedProducts(Object.keys(products).flatMap((key) => {
      return products[key];
    }));

    if (isPackageQuery) {
      setIsPackageQuery(false);
    }
  };

  const createPricingObject = (products) => {
    const activeItems = getFlatActiveProducts(products);
    const {
      originalPrice,
      dollarOffPromo,
      mapAboveOriginalPrice,
      dollarOff,
      mapDetail,
      mapExists,
      totalPrice,
      hideTotal,
      promotionalDiscounts
    } = getPricingInfo(
      activeItems,
      applianceDeliveryThreshold
    );

    const pricingObject = {
      pricing: {
        promotionalAdjustments: promotionalDiscounts,
        original: originalPrice,
        promotion: {
          dollarOff: dollarOff + dollarOffPromo,
          percentageOff: ((dollarOff + dollarOffPromo) * 100) / originalPrice
        },
        value: totalPrice,
        mapAboveOriginalPrice,
        message: mapExists ? 'see final price in cart' : null
      }
    };
    if (mapDetail?.mapPolicy !== null) {
      pricingObject.pricing.mapDetail = mapDetail;
    }
    return { pricingObject, hideTotal };
  };

  useEffect(() => {
    if (data) {
      if (isPackageQuery && !!data.packages[0].products) {
        setMetaData(data?.packages?.[0]?.metadata);
        groupProductsFromPackagesCall(data.packages[0].products);
      } else if (data.products) {
        groupProductsFromProductsCall(data.products);
      }
    }
  }, [data]);

  const breadCrumbs = metaData?.taxonomy?.breadCrumbs?.length ? metaData?.taxonomy?.breadCrumbs?.reduce(
    (acc, crumb, index, crumbs) => {
      if (crumb?.url === '/c/kitchen_appliance_packages') {
        acc.push({
          ...crumb,
          url: '/b/Appliances-Kitchen-Appliance-Packages/N-5yc1vZ2fkpfuj'
        });
      } else if (index === crumbs.length - 1) {
        acc.push({ label: `${metaData?.brandName || ''} ${metaData?.name || ''}`, url: location.pathname });
      } else {
        acc.push(crumb);
      }

      return acc;
    }, []
  ) : [];

  useEffect(() => {
    triggerPackageAnalytics(data?.packages, storeId, metaData, breadCrumbs, packageId, isCustomerIdentified);
  }, [data?.packages]);

  const hasProductGroups = !!Object.keys(productGroups).length;

  if (loading && !hasProductGroups) return <ProductSetPlaceholder height={isMobile ? 1189 : 852.2} />;

  const { pricingObject, hideTotal } = createPricingObject(productGroups);

  const buyboxNode = (
    <>
      {!hideTotal ? (
        <div className="appliance-package__price">
          <Price
            promoId={promoId?.toString()}
            channel={channel}
            product={pricingObject}
            itemId=""
            hideBadge
            clsRemediation={{
              placeholders: true,
              preservePlaceholders: true
            }}
          />
        </div>
      ) : (
        <div className="sui-flex lg:sui-w-fit sui-w-full sui-pb-2">
          {/* eslint-disable max-len */}
          <Tooltip title="We've set this item's price lower than the manufacturer will let us advertise, so you'll have to add it to your cart to see our price. If you decide you don't want to purchase the item, you can remove it from your cart.">
            <Typography component="span" weight="bold" tabIndex="0" role="button" decoration="underline">
              See Lower Price In Cart
            </Typography>
          </Tooltip>
        </div>
      )}
      <div className="appliance-package__atc">
        <AddToCart
          cartOptions={getCartOptions(getFlatActiveProducts(productGroups), metaData, channel)}
          cartReqParams={buildCartRequestParams(getFlatActiveProducts(productGroups), zipCode)}
          disabled={getFlatActiveProducts(productGroups).length === 0}
        >
          Add Package to Cart
        </AddToCart>
      </div>
    </>
  );

  const headerNode = (
    <Row className="appliance-package__header">
      <Col xs="12" sm="7" flatten>
        <Image src={metaData?.brandImage?.url} width="100" alt="Brand Image" />
        {isMobile ? (<h1 className="appliance-package__title">{metaData?.name} </h1>) : (
          <Heading
            className="apliance-package__title"
            title={metaData?.name}
            underline={false}
            noMargin
            flatten
          />
        )}
      </Col>
      {isMobile && (
        <StickyWithHeaderObserver
          boxShadow
          className="appliance-package__buybox-sticky"
          position="bottom"
          stickyByDefault
          visibleByDefault
        >
          <Col flatten className="appliance-package__buybox--sticky">
            {buyboxNode}
          </Col>
        </StickyWithHeaderObserver>
      )}
      {!isMobile && (
        <Col xs="12" sm="5" flatten className="appliance-package__buybox">
          {buyboxNode}
        </Col>
      )}
    </Row>
  );

  return (
    <>
      <ErrorBoundary name="redirector">
        <PackageRedirector />
      </ErrorBoundary>
      <ErrorBoundary name="appliance-package-metadata">
        <QueryProvider
          cacheKey="packages-metadata"
          dataSource="packages"
          defaultVariables={{
            isBrandPricingPolicyCompliant
          }}
        >
          <Metadata>
            <PackagesMetadata params={routerParams} />
          </Metadata>
        </QueryProvider>
      </ErrorBoundary>
      {!isMobile && (
        <ErrorBoundary name="sticky">
          <div className="appliance-package__sticky-wrapper">
            <StickyWithHeaderObserver
              boxShadow
              className="appliance-package__sticky"
              stickyType="forwardRef"
              stickyByDefault
              forwardRef={stickyRef}
              position="top"
            >
              <Row className="isBound">
                {headerNode}
              </Row>
            </StickyWithHeaderObserver>
          </div>
        </ErrorBoundary>
      )}
      <ErrorBoundary name="pip-master">
        <Row className="isBound appliance-package">
          <div className="appliance-package__breadcrumbs">
            <Col>
              <ErrorBoundary name="breadcrumbs">
                <Breadcrumbs staticCrumbs={breadCrumbs} />
              </ErrorBoundary>
            </Col>
          </div>
          <Row>
            <ErrorBoundary name="header-node">
              <span className="appliance-package__ref" ref={stickyRef} />
              {headerNode}
            </ErrorBoundary>
          </Row>
          <Row className="appliance-package__main-package">
            <Col xs="12" sm="5" md="7">
              <ErrorBoundary name="four-appliance-template">
                <div className="appliance-package__image-grid">
                  <FourApplianceTemplate
                    handlePlaceholderClick={(newProduct, productGroupLabel) => {
                      updateProducts({
                        ...productGroups,
                        [productGroupLabel]: [{
                          ...newProduct.product,
                          active: true,
                          removed: false,
                          swappedProducts: [],
                          info: {
                            ...newProduct.product.info,
                            productGroup: productGroupLabel
                          }
                        }]
                      });
                    }}
                    images={getDynamicImages(flattenProductGroups(productGroups))}
                  />
                </div>
              </ErrorBoundary>
            </Col>
            <Col xs="12" sm="7" md="5">
              <ErrorBoundary name="product-set">
                <QueryProvider
                  cacheKey="package-pod"
                  dataSource="packages"
                  defaultVariables={{
                    isBrandPricingPolicyCompliant
                  }}
                >
                  <ProductSet
                    isManagedPackage
                    packagePriorities={APPLIANCE_PACKAGE_PRIORITIES}
                    productGroups={productGroups}
                    updateProducts={updateProducts}
                    loadingState={loadingState}
                    setLoadingState={setLoadingState}
                    hideTotal={hideTotal}
                  />
                </QueryProvider>
              </ErrorBoundary>
            </Col>
          </Row>
        </Row>
      </ErrorBoundary>
    </>
  );
};

AppliancePackage.displayName = 'AppliancePackagePage';

const packagesDataModel = {
  packages:
    params({ packageId: string().isRequired(), storeId: string().isRequired(), limit: number() })
      .arrayOf(
        shape({
          metadata: shape({
            packageId: string(),
            title: string(),
            brandName: string(),
            brandImage: shape({ url: string() }),
            heroImage: shape({ url: string() }),
            image: shape({ url: string() }),
            defaultImage: shape({ url: string() }),
            packageCategory: string(),
            name: string(),
            taxonomy: shape({ breadCrumbs: shape({ url: string(), label: string() }) })
          }),
          products: params({}).arrayOf(ProductSet.dataModel.product)
        }))
};

const productsDataModel = {
  products: params({ itemIds: arrayOf(string().isRequired()).isRequired(), dataSource: string() }).arrayOf(
    extend({},
      ProductSet.dataModel.product,
      Price.dataModel.product,
      ProductPod.dataModel.product,
    )
  )
}

const dataModel = extend({}, packagesDataModel, productsDataModel, Price.dataModel, Breadcrumbs.dataModel);

AppliancePackage.dataModel = dataModel;
